import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const services = [
    {
      title: 'Carpentry Installation',
      description: 'Expert installation of interior and exterior carpentry, doors, windows, and custom woodwork with precision and attention to detail.',
      image: 'https://public.readdy.ai/ai/img_res/2e6be10fc52d2e89286d55e700957828.jpg',
      icon: 'ri-hammer-line'
    },
    {
      title: 'Land Preparation',
      description: 'Complete groundwork services including excavation, leveling, and site preparation for solid foundations and optimal construction conditions.',
      image: 'https://readdy.ai/api/search-image?query=heavy%20excavator%20machinery%20preparing%20construction%20site%20ground%20leveling%20terrain%20for%20building%20foundation%20with%20professional%20operator%20in%20clean%20organized%20worksite%20showing%20precision%20earthwork%20and%20land%20preparation&width=600&height=800&seq=terr001&orientation=portrait',
      icon: 'ri-truck-line'
    },
    {
      title: 'Floor & Wall Coverings',
      description: 'Professional installation of tiles, parquet, stone, and plaster finishes with meticulous craftsmanship and lasting quality.',
      image: 'https://readdy.ai/api/search-image?query=skilled%20craftsman%20installing%20premium%20ceramic%20tiles%20on%20modern%20bathroom%20wall%20with%20precise%20alignment%20and%20clean%20grout%20lines%20showing%20professional%20tile%20work%20and%20attention%20to%20detail%20in%20well%20lit%20construction%20environment&width=600&height=800&seq=reves001&orientation=portrait',
      icon: 'ri-layout-grid-line'
    },
    {
      title: 'Demolition Services',
      description: 'Safe and controlled demolition and structural preparation with full compliance to safety regulations and environmental standards.',
      image: 'https://readdy.ai/api/search-image?query=controlled%20demolition%20of%20interior%20walls%20in%20commercial%20building%20renovation%20with%20safety%20equipment%20dust%20control%20and%20professional%20demolition%20crew%20following%20strict%20safety%20protocols%20in%20organized%20construction%20site&width=800&height=600&seq=demo001&orientation=landscape',
      icon: 'ri-delete-bin-line'
    }
  ];

  const benefits = [
    {
      icon: 'ri-tools-line',
      title: 'Professional Execution',
      description: 'Skilled craftsmen with years of experience delivering exceptional results on every project, large or small.'
    },
    {
      icon: 'ri-time-line',
      title: 'On-Time Delivery',
      description: 'We respect your schedule and complete projects within agreed timelines without compromising quality.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety & Compliance',
      description: 'Full adherence to safety regulations and building codes, ensuring secure and compliant construction work.'
    },
    {
      icon: 'ri-award-line',
      title: 'Quality Materials',
      description: 'We use only premium materials from trusted suppliers to guarantee durability and long-lasting results.'
    }
  ];

  const projects = [
    {
      category: 'Carpentry',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20custom%20wooden%20staircase%20installation%20in%20luxury%20home%20interior%20showing%20expert%20carpentry%20craftsmanship%20with%20elegant%20handrails%20and%20precise%20joinery%20work%20in%20modern%20residential%20space%20with%20natural%20wood%20finish&width=600&height=800&seq=proj001&orientation=portrait'
    },
    {
      category: 'Renovation',
      image: 'https://readdy.ai/api/search-image?query=complete%20modern%20apartment%20renovation%20showing%20before%20and%20after%20transformation%20with%20new%20flooring%20walls%20and%20ceiling%20in%20bright%20contemporary%20living%20space%20demonstrating%20professional%20construction%20quality%20and%20clean%20finish%20work&width=900&height=600&seq=proj002&orientation=landscape'
    },
    {
      category: 'Coverings',
      image: 'https://readdy.ai/api/search-image?query=luxury%20bathroom%20with%20premium%20marble%20tile%20installation%20on%20walls%20and%20floor%20showing%20perfect%20alignment%20and%20professional%20grouting%20work%20in%20elegant%20modern%20bathroom%20with%20high%20end%20fixtures%20and%20clean%20lines&width=600&height=600&seq=proj003&orientation=squarish'
    },
    {
      category: 'Land Preparation',
      image: 'https://readdy.ai/api/search-image?query=construction%20site%20with%20leveled%20prepared%20ground%20ready%20for%20foundation%20work%20showing%20professional%20land%20grading%20and%20compaction%20with%20excavator%20equipment%20in%20background%20under%20clear%20sky%20demonstrating%20quality%20site%20preparation&width=800&height=600&seq=proj004&orientation=landscape'
    },
    {
      category: 'Carpentry',
      image: 'https://readdy.ai/api/search-image?query=custom%20built%20in%20wooden%20cabinets%20and%20shelving%20in%20modern%20kitchen%20showing%20expert%20carpentry%20installation%20with%20precise%20measurements%20and%20smooth%20finish%20in%20contemporary%20home%20interior%20with%20quality%20craftsmanship%20details&width=600&height=800&seq=proj005&orientation=portrait'
    },
    {
      category: 'Demolition',
      image: 'https://readdy.ai/api/search-image?query=safe%20controlled%20interior%20demolition%20work%20in%20commercial%20building%20showing%20professional%20crew%20removing%20old%20structures%20with%20proper%20safety%20equipment%20and%20dust%20control%20measures%20in%20organized%20construction%20environment&width=600&height=600&seq=proj006&orientation=squarish'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d51samveagc3l4h0tcug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setShowSuccessPopup(true);
        setFormData({ name: '', phone: '', email: '', message: '' });
        setTimeout(() => {
          setShowSuccessPopup(false);
          setSubmitStatus('idle');
        }, 4000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Success Popup */}
      <AnimatePresence>
        {showSuccessPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          >
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 10 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full text-center relative overflow-hidden"
            >
              {/* Building Animation Background */}
              <div className="absolute inset-0 pointer-events-none">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: "100%" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-orange/5 to-transparent"
                />
              </div>

              {/* Building Blocks Animation */}
              <div className="relative mb-6">
                <motion.div className="flex justify-center items-end space-x-2 h-24">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: `${(i + 1) * 16}px`, opacity: 1 }}
                      transition={{ delay: i * 0.1, duration: 0.4 }}
                      className="w-8 bg-orange rounded-t"
                    />
                  ))}
                </motion.div>
              </div>

              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                className="w-16 h-16 mx-auto mb-4 bg-orange rounded-full flex items-center justify-center"
              >
                <i className="ri-check-line text-white text-3xl"></i>
              </motion.div>

              {/* Text */}
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="text-2xl font-bold text-charcoal mb-2"
              >
                Message Sent!
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="text-textGrey"
              >
                Thank you for contacting us. We'll get back to you soon!
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20construction%20workers%20team%20building%20modern%20residential%20structure%20with%20scaffolding%20and%20safety%20equipment%20on%20active%20construction%20site%20showing%20skilled%20craftsmanship%20and%20teamwork%20in%20bright%20daylight%20with%20concrete%20and%20steel%20framework&width=1920&height=1080&seq=hero001&orientation=landscape"
            alt="Construction site"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full px-6 lg:px-12 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-6">
              Construction & Rehabilitation with Guarantee
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 font-medium mb-10 leading-relaxed">
              Professional carpentry, renovations, land preparation, and demolition services
            </p>
            <div className="flex flex-col sm:flex-row gap-5">
              <a
                href="#contact"
                className="inline-flex items-center justify-center space-x-2 bg-charcoal text-white px-8 py-4 font-semibold text-base tracking-wide hover:bg-charcoal/90 transition-all border-b-4 border-orange whitespace-nowrap cursor-pointer"
              >
                <span>Request a Quote</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <a
                href="#projects"
                className="inline-flex items-center justify-center space-x-2 bg-transparent text-white px-8 py-4 font-semibold text-base tracking-wide border-2 border-white hover:bg-white hover:text-charcoal transition-all whitespace-nowrap cursor-pointer"
              >
                <span>View Our Work</span>
              </a>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
              Our Services
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-charcoal">
              What We Do Best
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative group overflow-hidden rounded-lg shadow-lg cursor-pointer ${
                  index === 0 || index === 3 ? 'lg:col-span-1' : 'lg:col-span-1'
                } ${index === 1 ? 'lg:row-span-2' : ''}`}
              >
                <div className="relative h-96 lg:h-full overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/90 transition-all duration-300"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="flex items-center space-x-3 mb-3">
                    <i className={`${service.icon} text-2xl text-orange`}></i>
                    <h3 className="text-2xl font-bold tracking-wide">{service.title}</h3>
                  </div>
                  <p className="text-white/90 leading-relaxed">{service.description}</p>
                  <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-arrow-right-line text-orange text-2xl"></i>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-concrete py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://readdy.ai/api/search-image?query=close%20up%20of%20skilled%20construction%20worker%20hands%20installing%20precision%20building%20element%20with%20professional%20tools%20showing%20expert%20craftsmanship%20attention%20to%20detail%20and%20quality%20workmanship%20in%20bright%20construction%20environment%20with%20focus%20on%20technique&width=800&height=1000&seq=why001&orientation=portrait"
                alt="Quality workmanship"
                className="w-full h-full object-cover object-top rounded-lg shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
                  Why Choose Us
                </p>
                <h2 className="text-4xl lg:text-5xl font-bold text-charcoal mb-6">
                  Your Trusted Construction Partner
                </h2>
              </div>

              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className="w-12 h-12 flex items-center justify-center border-2 border-charcoal rounded flex-shrink-0">
                    <i className={`${benefit.icon} text-2xl text-charcoal`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-charcoal mb-2 tracking-wide">
                      {benefit.title}
                    </h3>
                    <p className="text-textGrey leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}

              <a
                href="/about"
                className="inline-flex items-center space-x-2 bg-charcoal text-white px-8 py-4 font-semibold text-base tracking-wide hover:bg-charcoal/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>Learn More About Us</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Gallery Section */}
      <section id="projects" className="py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-bold tracking-widest text-orange uppercase mb-4">
              Our Projects
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-charcoal">
              Quality Work in Action
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative group overflow-hidden rounded-lg shadow-lg cursor-pointer h-80"
              >
                <img
                  src={project.image}
                  alt={project.category}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                    <span className="inline-block bg-orange text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                      {project.category}
                    </span>
                    <p className="text-white font-semibold text-lg">View Project</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="relative py-32 px-6 lg:px-12">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20construction%20site%20at%20golden%20hour%20sunset%20with%20warm%20orange%20lighting%20showing%20modern%20building%20structure%20and%20professional%20construction%20equipment%20creating%20inspiring%20atmosphere%20with%20dramatic%20sky%20and%20architectural%20elements%20in%20silhouette&width=1920&height=1080&seq=cta001&orientation=landscape"
            alt="Contact us"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-white/90 mb-12">
              Get in touch today for a free consultation and detailed quote
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your Name"
                  required
                  className="w-full px-6 py-4 bg-white text-charcoal placeholder-textGrey focus:outline-none focus:ring-2 focus:ring-orange text-sm"
                />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                  required
                  className="w-full px-6 py-4 bg-white text-charcoal placeholder-textGrey focus:outline-none focus:ring-2 focus:ring-orange text-sm"
                />
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email Address"
                required
                className="w-full px-6 py-4 bg-white text-charcoal placeholder-textGrey focus:outline-none focus:ring-2 focus:ring-orange text-sm"
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your project"
                rows={4}
                maxLength={500}
                required
                className="w-full px-6 py-4 bg-white text-charcoal placeholder-textGrey focus:outline-none focus:ring-2 focus:ring-orange resize-none text-sm"
              ></textarea>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto px-12 py-4 bg-orange text-white font-bold text-lg tracking-wide hover:bg-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {submitStatus === 'error' && (
                <p className="text-white bg-red-600 px-4 py-2 rounded">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
